import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

import { environment } from '../../../../environments/environment';
import { DOCTOR_SLUG_PREFIX, GIVE_UP_MS, SiteDataState, departmentSlugOf, normalizeName } from './site-utils';

export interface SiteDoctor {
  id: string;
  slug: string;
  name: string;
  departmentId: string;
  departmentName: string;
  departmentSlug: string;
  about: string;
  qualification: string;
  education: string;
  experience: string;
  specialities: string;
  languages: string;
  photo: string;
  hasPhoto: boolean;
}

@Injectable()
export class SiteDoctorService implements OnDestroy {
  readonly state$ = new BehaviorSubject<SiteDataState>('loading');
  readonly doctors$ = new BehaviorSubject<SiteDoctor[]>([]);

  private key = '';
  private readonly giveUpTimer: number;

  constructor(private http: HttpClient) {
    this.giveUpTimer = window.setTimeout(() => {
      if (this.state$.value === 'loading') { this.state$.next('fallback'); }
    }, GIVE_UP_MS);
  }

  ngOnDestroy() { window.clearTimeout(this.giveUpTimer); }

  configure(tenantId: string, branchId: string) {
    if (!tenantId || !branchId || this.key === tenantId + '/' + branchId) { return; }
    this.key = tenantId + '/' + branchId;
    this.load(tenantId, branchId);
  }

  forDepartmentName(name: string): SiteDoctor[] {
    return this.doctors$.value.filter(d => normalizeName(d.departmentName) === normalizeName(name));
  }

  forDepartmentId(id: string): SiteDoctor[] {
    return this.doctors$.value.filter(d => d.departmentId === id);
  }

  bySlug(slug: string): SiteDoctor | undefined {
    return this.doctors$.value.find(d => d.slug === slug);
  }

  private load(tenantId: string, branchId: string) {
    const base = `${environment.apiUrl}/publicsite/${tenantId}/${branchId}`;
    this.http.get<any>(`${base}/doctors`).subscribe(response => {
      const records: any[] = (response && response.details) || [];
      const doctors = records.map(r => this.toDoctor(r));
      this.doctors$.next(doctors);
      this.state$.next(doctors.length ? 'ready' : 'fallback');
      records.forEach((r, i) => this.loadPhoto(tenantId, branchId, r.photo, doctors[i]));
    }, () => this.state$.next('fallback'));
  }

  private toDoctor(r: any): SiteDoctor {
    const female = /^f/i.test(r.gender || '');
    return {
      id: r.id,
      slug: DOCTOR_SLUG_PREFIX + r.id,
      name: r.name || '',
      departmentId: r.departmentId,
      departmentName: r.departmentName || '',
      departmentSlug: departmentSlugOf(r.departmentId, r.departmentName),
      about: (r.about || '').trim(),
      qualification: (r.qualification || '').trim(),
      education: (r.education || '').trim(),
      experience: (r.experience || '').trim(),
      specialities: (r.specialities || '').trim(),
      languages: (r.languages || '').trim(),
      photo: 'assets/images/well-spring-site/' + (female ? 'doctor-female.jpg' : 'doctor-male.jpg'),
      hasPhoto: false
    };
  }

  private loadPhoto(tenantId: string, branchId: string, record: any, doctor: SiteDoctor) {
    if (!record) { return; }
    const url = `${environment.apiUrl}/ImageAtchDet/${tenantId}/${branchId}/download`;
    this.http.post<string>(url, { ...record, id: record.id, isCreate: true, isEdit: true }).subscribe(image => {
      if (!image) { return; }
      doctor.photo = 'data:image/jpeg;base64,' + image;
      doctor.hasPhoto = true;
      this.doctors$.next(this.doctors$.value.slice());
    }, () => {});
  }
}
